// The on-ramp offer and the lit course line (R3075, ui-map-interface.md §8
// "The on-ramp offer", §6.1; v2-the-deep.md §7.2 "The lane course").
//
// `LaneWaters` keeps what the lane course of `lanecourse.ts` reads, from
// the picture of the client: the lit water of the fairways (the hello),
// the lane cells and the tracks of the shipping lanes of the faction (the
// own plots with LANE and the mates' lanes of the Empire frame), and the
// armed Fanals of the faction (the sea object rows). It builds each
// part again only when its source changed, and `rev` moves only then, so
// a reader caches on it (§10: no heavy work once a frame). The wire sends
// the lanes and the fairways only under `routes` and the lights only under
// `sapper`, so the keeper reads no flag.
//
// `LaneOfferDesk` holds the offers of the own fleets: after a MOVE to a
// far point that a lane serves faster, the card of the fleet offers the
// lane course for `offer_sec` wall seconds. Any other order of the fleet
// takes the offer away. A press of VIA LANE (or shift with v) sends the
// lane course, computed again from where the fleet stands then.

import { SeaObjectKind, type CatalogRules, type CoursePlot, type Fairway, type FairwayBuoy, type SeaObjectSpec } from './gen/proto/aquaphobia';
import { laneTracks, type ChainTrack } from './lanechain';
import { bestLaneCourse, litMask, litRuns, markLaneCells, type LaneLift, type LanePt, type LaneRoute, type LaneWater } from './lanecourse';
import { COLS, mpxToPx, ROWS } from './world';

/** What the keeper reads of the state. `GameState` satisfies it. */
export interface LaneStore {
  readonly fairways: readonly Fairway[];
  readonly buoys: readonly FairwayBuoy[];
  readonly plots: readonly CoursePlot[];
  readonly factionLanes: readonly CoursePlot[];
  readonly faction: number;
  readonly rules: CatalogRules;
  seaObject(kind: SeaObjectKind): SeaObjectSpec | undefined;
  readonly seaObjects: {
    readonly rev: number;
    readonly list: readonly { faction: number; kind: SeaObjectKind; x: number; y: number; armed: boolean; heard: boolean }[];
  };
}

/** One track of a lane of the faction, with the name of its lane. */
export interface NamedTrack { track: ChainTrack; name: string }

/** The lane course of an offer, with the name of its lane. */
export interface LaneCourse extends LaneRoute { name: string }

/** The key of a list of lanes: the owners, the ids, the names and the
 *  points. */
function lanesKey(lanes: readonly CoursePlot[]): string {
  return lanes.map((p) => `${p.owner}:${p.id}:${p.name}:${p.legs.map((w) => `${w.xMpx},${w.yMpx},${w.loop ? 1 : 0}${w.passes}`).join(';')}`).join('|');
}

export class LaneWaters {
  private readonly empty: Uint8Array = new Uint8Array(0);
  private litSrc: unknown = null;
  private litCols = 0;
  private lit = this.empty;
  private laneKey = '';
  private plotsSrc: unknown = null;
  private matesSrc: unknown = null;
  private lane = this.empty;
  private tracksV: NamedTrack[] = [];
  private bare: ChainTrack[] = [];
  private lightRev = -1;
  private lightKey = '';
  private lights: LaneLift[] = [];
  private buoyMul = 0;
  private laneMul = 0;
  private waterV: LaneWater | null = null;
  private revV = 0;

  constructor(private readonly s: LaneStore) {}

  /** A counter that moves when the water, the lanes or the lights change. */
  get rev(): number { this.sync(); return this.revV; }

  /** The water the lift reads now. */
  water(): LaneWater { this.sync(); return this.waterV!; }

  /** The tracks of the lanes of the faction, own lanes first. */
  tracks(): readonly NamedTrack[] { this.sync(); return this.tracksV; }

  private sync(): void {
    const s = this.s;
    let moved = false;
    if (s.fairways !== this.litSrc || this.litCols !== COLS * ROWS) {
      this.litSrc = s.fairways;
      this.litCols = COLS * ROWS;
      const chains = s.fairways.flatMap((f) => f.chains.map((c) => c.cells));
      this.lit = chains.length > 0
        ? litMask(chains, s.buoys.map((b) => ({ x: mpxToPx(b.xMpx), y: mpxToPx(b.yMpx), chain: b.chain })))
        : new Uint8Array(COLS * ROWS);
      moved = true;
    }
    // An Empire frame replaces both lists; the key then says whether a
    // lane moved, so a frame that changed no lane walks no cell.
    const fresh = s.plots !== this.plotsSrc || s.factionLanes !== this.matesSrc;
    this.plotsSrc = s.plots;
    this.matesSrc = s.factionLanes;
    const lanes = fresh ? [...s.plots.filter((p) => p.lane), ...s.factionLanes] : [];
    const key = fresh ? `${COLS}x${ROWS}|${lanesKey(lanes)}` : this.laneKey;
    if (key !== this.laneKey) {
      this.laneKey = key;
      this.lane = new Uint8Array(COLS * ROWS);
      this.tracksV = [];
      for (const p of lanes) {
        markLaneCells(p.legs.map((w) => ({ x: mpxToPx(w.xMpx), y: mpxToPx(w.yMpx), loop: w.loop, passes: w.passes })), this.lane);
        for (const t of laneTracks(p)) this.tracksV.push({ track: t, name: p.name });
      }
      this.bare = this.tracksV.map((t) => t.track);
      moved = true;
    }
    if (s.seaObjects.rev !== this.lightRev) {
      this.lightRev = s.seaObjects.rev;
      const spec = s.seaObject(SeaObjectKind.SEA_OBJECT_KIND_LANE_LIGHT);
      const reach = spec?.reachPx ?? 0, mul = spec?.speedMul || 1;
      const lights: LaneLift[] = [];
      if (reach > 0) {
        for (const o of s.seaObjects.list) {
          if (o.kind === SeaObjectKind.SEA_OBJECT_KIND_LANE_LIGHT && o.armed && !o.heard && o.faction === s.faction) {
            lights.push({ x: o.x, y: o.y, reach2: reach * reach, mul });
          }
        }
      }
      const lk = lights.map((l) => `${l.x},${l.y},${l.reach2},${l.mul}`).join('|');
      if (lk !== this.lightKey) { this.lightKey = lk; this.lights = lights; moved = true; }
    }
    if (s.rules.buoySpeedMul !== this.buoyMul) { this.buoyMul = s.rules.buoySpeedMul; moved = true; }
    if (s.rules.laneSpeedMul !== this.laneMul) { this.laneMul = s.rules.laneSpeedMul; moved = true; }
    if (moved || !this.waterV) {
      this.waterV = { cols: COLS, rows: ROWS, lit: this.lit, lane: this.lane, lights: this.lights, buoyMul: this.buoyMul || 1, laneMul: this.laneMul || 1 };
      this.revV++;
    }
  }

  /**
   * The lane course from `from` to `to` by the rule of the pack
   * (`route_lane_use_margin`, `route_lane_use_min_px`, `route_legs_max`),
   * or null while the world sends no rule, no lane, or no lane saves the
   * margin. The cost is bounded by the lanes.
   */
  course(from: LanePt, to: LanePt): LaneCourse | null {
    const r = this.s.rules;
    const tracks = this.tracks();
    if (tracks.length === 0 || !(r.routeLaneUseMinPx > 0 || r.routeLaneUseMargin > 0)) return null;
    const best = bestLaneCourse(this.bare, from, to, this.water(), r.routeLaneUseMargin, r.routeLaneUseMinPx, r.routeLegsMax || 8);
    return best ? { ...best, name: tracks[best.track].name } : null;
  }

  /** The lit runs of a course from `from` through the points, as x0, y0,
   *  x1, y1 in world px. */
  runs(from: LanePt, pts: readonly LanePt[]): number[] {
    return litRuns(this.water(), from, pts);
  }
}

/** One standing offer: the fleet, the target of its MOVE, the lane course
 *  of the moment of the order and its saving in map seconds. */
export interface LaneOffer {
  fleetId: number;
  tx: number;
  ty: number;
  legs: LanePt[];
  lane: string;
  saveMapSec: number;
  atMs: number;
}

/** What the desk reads of a fleet: an own hull at sea, its point, its
 *  order now and its speed in px each map second (0: no speed). */
export interface OfferFleet { x: number; y: number; mission: number; tx: number; ty: number; pxPerMapSec: number }

export interface LaneOfferDeps {
  waters: LaneWaters;
  fleet(fleetId: number): OfferFleet | null;
  /** The mission id of a MOVE: an offer stands while the fleet sails it. */
  move: number;
  /** The wall ms an offer stands, and the wall ms after the order before
   *  the frame of the fleet must show the MOVE. */
  lifeMs: number;
  graceMs: number;
  now(): number;
}

export class LaneOfferDesk {
  private readonly offers = new Map<number, LaneOffer>();

  constructor(private readonly d: LaneOfferDeps) {}

  /** A MOVE of the fleet to (tx, ty) went out: the desk offers the lane
   *  course when a lane saves the margin. It answers the offer. */
  give(fleetId: number, tx: number, ty: number): LaneOffer | null {
    this.offers.delete(fleetId);
    const f = this.d.fleet(fleetId);
    if (!f || !(f.pxPerMapSec > 0)) return null;
    const c = this.d.waters.course({ x: f.x, y: f.y }, { x: tx, y: ty });
    if (!c) return null;
    const o: LaneOffer = {
      fleetId, tx, ty, legs: c.legs, lane: c.name,
      saveMapSec: (c.direct - c.time) / f.pxPerMapSec, atMs: this.d.now(),
    };
    this.offers.set(fleetId, o);
    return o;
  }

  /** The offer that stands for the fleet now, or null. An offer lapses
   *  after its life, when the fleet left the picture, and when the frame
   *  of the fleet shows another order after the grace. */
  of(fleetId: number): LaneOffer | null {
    const o = this.offers.get(fleetId);
    if (!o) return null;
    const age = this.d.now() - o.atMs;
    const f = this.d.fleet(fleetId);
    const other = f !== null && age > this.d.graceMs
      && (f.mission !== this.d.move || Math.abs(f.tx - o.tx) > 2 || Math.abs(f.ty - o.ty) > 2);
    if (age > this.d.lifeMs || !f || other) {
      this.offers.delete(fleetId);
      return null;
    }
    return o;
  }

  /** Takes the offer of the fleet: the legs of the lane course from where
   *  the fleet stands now, or the legs of the offer when the course from
   *  here saves no margin any more; null with no offer. */
  take(fleetId: number): LanePt[] | null {
    const o = this.of(fleetId);
    if (!o) return null;
    this.offers.delete(fleetId);
    const f = this.d.fleet(fleetId);
    const now = f ? this.d.waters.course({ x: f.x, y: f.y }, { x: o.tx, y: o.ty }) : null;
    return now?.legs ?? o.legs;
  }

  /** Another order of the fleet went out: its offer goes. */
  drop(fleetId: number): void {
    this.offers.delete(fleetId);
  }
}
